<template>
  <div>
    <v-dialog
      v-model="isOpen"
      max-width="1250"
      scrollable
    >
      <v-card class="mau-list">
        <div class="mau-list__header">
          <div class="d-flex align-center">
            <div class="mau-list__icon">
              <v-icon color="white">{{ isLcd ? 'mdi-image-multiple-outline' : 'mdi-music-box-multiple-outline' }}</v-icon>
            </div>
            <div>
              <div class="mau-list__title">{{ listTitle }}</div>
              <div class="mau-list__subtitle">{{ listSubtitle }}</div>
            </div>
          </div>
          <v-btn icon dark @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="mau-list__body">
          <div class="mau-list__toolbar">
            <v-text-field
              v-model="search"
              dense
              outlined
              hide-details
              clearable
              prepend-inner-icon="mdi-magnify"
              placeholder="Фильтр на странице: код, файл, автор"
              class="mau-list__search"
            />
            <div v-if="totalJournal" class="mau-list__total">
              Всего: <strong>{{ totalJournal }}</strong>
            </div>
          </div>

          <v-data-table
            class="table mau-list__table"
            dense
            :headers="headers"
            :items="filteredAudio"
            :options.sync="options"
            :server-items-length="totalJournal"
            :loading="loadingAudioUpload"
            :footer-props="{
              'items-per-page-options': [10, 20, 50],
              showFirstLastPage: true,
            }"
            item-key="mass_upload_id"
            disable-sort
          >
            <template v-slot:item.doorphone_count="{ item }">
              <v-chip x-small label color="blue lighten-5" text-color="primary">
                <v-icon left x-small>mdi-doorbell-video</v-icon>
                {{ item.doorphone_count }}
              </v-chip>
            </template>

            <template v-slot:item.mass_upload_id="{ item }">
              <div class="mau-list__code">
                <span :title="item.mass_upload_id">{{ shortId(item.mass_upload_id) }}</span>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      x-small
                      class="ml-1"
                      v-bind="attrs"
                      v-on="on"
                      @click="copyId(item.mass_upload_id)"
                    >
                      <v-icon x-small>mdi-content-copy</v-icon>
                    </v-btn>
                  </template>
                  <span>Скопировать код</span>
                </v-tooltip>
              </div>
            </template>

            <template v-slot:item.file_name="{ item }">
              <div class="mau-list__file">
                <v-icon x-small color="primary" class="mr-1">{{ isLcd ? 'mdi-file-image-outline' : 'mdi-file-music-outline' }}</v-icon>
                <expandable-text
                  :text="item.real_name || item.file_name"
                  :max-length="20"
                  single-line-collapsed
                />
              </div>
            </template>

            <template v-slot:item.task_creating_date="{ item }">
              <span class="mau-list__date">{{ formatDate(item.task_creating_date) }}</span>
            </template>

            <template v-slot:item.schedule="{ item }">
              <span v-if="item.start_date || item.end_date" class="mau-list__schedule">
                {{ formatDate(item.start_date) }}
                <span class="mau-list__schedule-sep">→</span>
                {{ formatDate(item.end_date) }}
              </span>
              <span v-else class="mau-list__muted">сразу</span>
            </template>

            <template v-slot:item.user_name="{ item }">
              <div class="mau-list__author">
                <v-avatar size="22" color="indigo lighten-4" class="mr-2">
                  <span class="mau-list__avatar-letter">{{ authorInitial(item.user_name) }}</span>
                </v-avatar>
                <span>{{ item.user_name || '—' }}</span>
              </div>
            </template>

            <template v-slot:item.actions="{ item }">
              <div class="mau-list__actions">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="primary"
                      v-bind="attrs"
                      v-on="on"
                      :loading="!!isLoadingDetail[item.mass_upload_id]"
                      @click="openDetail(item.mass_upload_id)"
                    >
                      <v-icon small>mdi-eye-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Подробнее</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="primary"
                      v-bind="attrs"
                      v-on="on"
                      :loading="!!isDownloadingAudio[item.mass_upload_id]"
                      :disabled="!item.real_name && !item.file_name"
                      @click="downloadAudio(item.mass_upload_id)"
                    >
                      <v-icon small>{{ isLcd ? 'mdi-file-image-outline' : 'mdi-file-music-outline' }}</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ isLcd ? 'Скачать изображение' : 'Скачать аудио' }}</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="primary"
                      v-bind="attrs"
                      v-on="on"
                      :loading="!!isExporting[item.mass_upload_id]"
                      @click="downloadCsv(item.mass_upload_id)"
                    >
                      <v-icon small>mdi-file-delimited-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Скачать CSV</span>
                </v-tooltip>
              </div>
            </template>

            <template v-slot:no-data>
              <div class="mau-list__empty">
                <v-icon large color="grey lighten-1" class="mb-2">{{ isLcd ? 'mdi-image-off-outline' : 'mdi-music-off' }}</v-icon>
                <div>Загрузок пока нет</div>
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import moment from 'moment';
import watchTableOptions from '@/mixins/watchTableOptions';
import { DEFAULT_DATA_TABLE_OPTIONS } from '@/constants/data-table';
import { MASS_AUDIO_OPEN_RESULT_EVENT } from '@/components/massOpenDoorSound/MassAudioUploadResultDialog.vue';
import ExpandableText from '@/components/ExpandableText';

export default {
  name: 'LoadingAudioDoorOpen',
  mixins: [watchTableOptions],
  components: {
    ExpandableText,
  },
  props: {
    dialog: {
      type: Boolean,
      required: true,
    },
    loadingAudioUpload: {
      type: Boolean,
      required: true,
    },
    totalJournal: {
      type: Number,
      default: 0,
    },
    mediaType: {
      type: String,
      default: 'audio',
      validator: (v) => ['audio', 'lcd'].includes(v),
    },
  },
  data () {
    return {
      isOpen: false,
      options: { ...DEFAULT_DATA_TABLE_OPTIONS },
      search: '',
      isLoadingDetail: {},
      isDownloadingAudio: {},
      isExporting: {},
      headers: [
        { text: 'Домофоны', value: 'doorphone_count', width: 100 },
        { text: 'Код', value: 'mass_upload_id', width: 120 },
        { text: 'Файл', value: 'file_name', width: 300 },
        { text: 'Создано', value: 'task_creating_date', width: 140 },
        { text: 'Расписание', value: 'schedule', width: 100 },
        { text: 'Автор', value: 'user_name', width: 120 },
        { text: '', value: 'actions', width: 120, align: 'end' },
      ],
    };
  },
  watch: {
    dialog: {
      immediate: true,
      handler (open) {
        this.isOpen = open;
        if (open) {
          this.search = '';
          if (this.options.page !== 1 || this.options.itemsPerPage !== DEFAULT_DATA_TABLE_OPTIONS.itemsPerPage) {
            this.options = { ...DEFAULT_DATA_TABLE_OPTIONS };
          }
        }
      },
    },
    isOpen (open) {
      if (!open && this.dialog) {
        this.search = '';
        this.$emit('close');
      }
    },
    options (curr, prev) {
      this.handleOptionsChange(curr, prev, () => {
        this.reloadList();
      });
    },
  },
  computed: {
    ...mapGetters({
      audio: 'Doorphone/audio',
    }),
    isLcd () {
      return this.mediaType === 'lcd';
    },
    listTitle () {
      return this.isLcd
        ? 'Массовая загрузка изображений LCD'
        : 'Массовая загрузка аудиозаписи';
    },
    listSubtitle () {
      return this.isLcd
        ? 'История загрузок пользовательских изображений на RV-3434'
        : 'История загрузок звука открытия двери';
    },
    filteredAudio () {
      const q = (this.search || '').trim().toLowerCase();
      if (!q) return this.audio || [];
      return (this.audio || []).filter((item) => {
        const hay = [
          item.mass_upload_id,
          item.file_name,
          item.real_name,
          item.user_name,
        ].join(' ').toLowerCase();
        return hay.includes(q);
      });
    },
  },
  methods: {
    ...mapActions({
      audioUpload: 'Doorphone/audioUpload',
      downloadAudioFile: 'MassAudioUploadJobs/downloadAudioFile',
      exportResult: 'MassAudioUploadJobs/exportResult',
    }),
    close () {
      this.isOpen = false;
    },
    async reloadList () {
      try {
        const response = await this.audioUpload({
          options: {
            page: this.options.page,
            limit: this.options.itemsPerPage,
            media_type: this.mediaType,
          },
        });
        this.$emit('update:totalJournal', response.meta?.total || 0);
      } catch (e) {
        this.$flash_warning('Ошибка при загрузке списка');
      }
    },
    formatDate (value) {
      if (!value) return '—';
      const m = moment(value);
      return m.isValid() ? m.format('DD.MM.YYYY HH:mm') : value;
    },
    shortId (id) {
      if (!id) return '—';
      const s = String(id);
      if (s.length <= 12) return s;
      return `${s.slice(0, 6)}…${s.slice(-4)}`;
    },
    authorInitial (name) {
      const s = (name || '?').trim();
      return s.charAt(0).toUpperCase();
    },
    async copyId (id) {
      try {
        await navigator.clipboard.writeText(String(id));
        if (typeof this.$flash_success === 'function') {
          this.$flash_success('Код скопирован');
        }
      } catch (e) {
        this.$flash_warning('Не удалось скопировать');
      }
    },
    openDetail (massUploadId) {
      if (!massUploadId) return;
      this.$set(this.isLoadingDetail, massUploadId, true);
      window.dispatchEvent(new CustomEvent(MASS_AUDIO_OPEN_RESULT_EVENT, {
        detail: { massUploadId, mediaType: this.mediaType },
      }));
      this.$nextTick(() => {
        this.$set(this.isLoadingDetail, massUploadId, false);
      });
    },
    async downloadAudio (massUploadId) {
      this.$set(this.isDownloadingAudio, massUploadId, true);
      try {
        await this.downloadAudioFile(massUploadId);
      } catch (e) {
        this.$flash_warning(this.isLcd ? 'Не удалось скачать изображение' : 'Не удалось скачать аудиофайл');
      } finally {
        this.$set(this.isDownloadingAudio, massUploadId, false);
      }
    },
    async downloadCsv (massUploadId) {
      this.$set(this.isExporting, massUploadId, true);
      try {
        await this.exportResult(massUploadId);
      } catch (e) {
        this.$flash_warning('Не удалось скачать CSV');
      } finally {
        this.$set(this.isExporting, massUploadId, false);
      }
    },
  },
};
</script>

<style scoped>
.mau-list {
  overflow: hidden;
}
.mau-list__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px;
  background: linear-gradient(135deg, #0d4092, #1e88e5);
  color: #fff;
}
.mau-list__icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
}
.mau-list__title {
  font-weight: 700;
  font-size: 1.05rem;
  line-height: 1.25;
}
.mau-list__subtitle {
  font-size: 0.78rem;
  opacity: 0.85;
  margin-top: 2px;
}
.mau-list__body {
  padding: 16px 18px 8px !important;
}
.mau-list__toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}
.mau-list__search {
  max-width: 360px;
  flex: 1 1 240px;
}
.mau-list__total {
  font-size: 0.85rem;
  color: #607d8b;
  margin-left: auto;
}
.mau-list__code {
  display: inline-flex;
  align-items: center;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.8rem;
  color: #37474f;
}
.mau-list__file {
  display: flex;
  align-items: flex-start;
  min-width: 260px;
  max-width: 360px;
}
.mau-list__file .v-icon {
  margin-top: 2px;
  flex-shrink: 0;
}
.mau-list__file ::v-deep .expandable-text {
  min-width: 0;
  flex: 1;
}
.mau-list__table ::v-deep th:nth-child(3),
.mau-list__table ::v-deep td:nth-child(3) {
  min-width: 280px;
  width: 300px;
}
.mau-list__date {
  font-size: 0.85rem;
  white-space: nowrap;
}
.mau-list__schedule {
  font-size: 0.8rem;
  color: #455a64;
  white-space: nowrap;
}
.mau-list__schedule-sep {
  opacity: 0.5;
  margin: 0 2px;
}
.mau-list__muted {
  color: #90a4ae;
  font-size: 0.85rem;
}
.mau-list__author {
  display: flex;
  align-items: center;
  min-width: 0;
}
.mau-list__avatar-letter {
  font-size: 0.7rem;
  font-weight: 700;
  color: #283593;
}
.mau-list__actions {
  display: flex;
  justify-content: flex-end;
  white-space: nowrap;
}
.mau-list__empty {
  text-align: center;
  padding: 36px 12px;
  color: #90a4ae;
  font-size: 0.9rem;
}
</style>
